import { useEffect, useRef, useState } from "react";
import { useT } from "@/i18n/LanguageProvider";

function useCountUp(end, duration = 1800, start = 0) {
    const [val, setVal] = useState(start);
    const ref = useRef(null);
    const triggered = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !triggered.current) {
                        triggered.current = true;
                        const startTime = performance.now();
                        const tick = (now) => {
                            const t = Math.min(1, (now - startTime) / duration);
                            const eased = 1 - Math.pow(1 - t, 3);
                            setVal(Math.round(start + (end - start) * eased));
                            if (t < 1) requestAnimationFrame(tick);
                        };
                        requestAnimationFrame(tick);
                    }
                });
            },
            { threshold: 0.4 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [end, duration, start]);

    return [val, ref];
}

function Stat({ value, prefix, suffix, label, sub, testId, animated = true }) {
    const [count, ref] = useCountUp(animated ? value : 0);
    const display = animated ? count : value;
    return (
        <div data-testid={testId} ref={ref} className="space-y-4">
            <div className="font-display text-7xl md:text-9xl text-primary-foreground leading-none tracking-tighter">
                {prefix && <span className="text-accent">{prefix}</span>}
                {display}
                {suffix && <span className="text-accent">{suffix}</span>}
            </div>
            <div className="h-px w-16 bg-primary-foreground/30" aria-hidden="true" />
            <div>
                <div className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-1">
                    {label}
                </div>
                <p className="text-sm md:text-base text-primary-foreground/75 leading-relaxed max-w-xs">
                    {sub}
                </p>
            </div>
        </div>
    );
}

export default function Stats() {
    const { t } = useT();
    return (
        <section
            id="stats"
            data-testid="stats-section"
            aria-labelledby="stats-headline"
            className="relative py-24 md:py-40 bg-primary text-primary-foreground"
        >
            <div className="absolute inset-0 opacity-[0.04]" aria-hidden="true">
                <div
                    className="w-full h-full"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 1px 1px, rgba(247,244,235,0.6) 1px, transparent 0)",
                        backgroundSize: "32px 32px",
                    }}
                />
            </div>

            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex items-center gap-6 mb-14 text-primary-foreground/85">
                    <span aria-hidden="true" className="font-display italic text-6xl md:text-8xl text-primary-foreground/30 leading-none">
                        03
                    </span>
                    <div className="text-xs uppercase tracking-[0.3em] text-accent font-semibold">
                        {t("stats.chapter")}
                    </div>
                </div>

                <h2 id="stats-headline" className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-3xl mb-20">
                    {t("stats.headline.prefix")} <em className="text-accent">{t("stats.headline.accent")}</em>{t("stats.headline.suffix") ? ` ${t("stats.headline.suffix")}` : "."}
                </h2>

                <div className="grid md:grid-cols-3 gap-16 md:gap-12">
                    <Stat
                        testId="stat-tonnes"
                        value={62}
                        suffix="M+"
                        label={t("stats.tonnes.label")}
                        sub={t("stats.tonnes.sub")}
                    />
                    <div className="md:pt-16">
                        <Stat
                            testId="stat-value"
                            prefix="₹"
                            value={0}
                            label={t("stats.value.label")}
                            sub={t("stats.value.sub")}
                            animated={false}
                        />
                    </div>
                    <div className="md:pt-32">
                        <div data-testid="stat-potential" className="space-y-4">
                            <div className="font-display text-7xl md:text-9xl leading-none tracking-tighter">
                                <span className="text-accent">∞</span>
                            </div>
                            <div className="h-px w-16 bg-primary-foreground/30" aria-hidden="true" />
                            <div>
                                <div className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-1">
                                    {t("stats.potential.label")}
                                </div>
                                <p className="text-sm md:text-base text-primary-foreground/75 leading-relaxed max-w-xs">
                                    {t("stats.potential.sub")}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
